import classNames from 'classnames';
import { useState } from 'react';
import styled from 'styled-components';

import { FadeInOut } from '#src/atoms/animations/FadeInOut/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useWalletDrawerContext } from '#src/contexts/walletDrawer/index.tsx';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import { Currency } from './Currency/index.tsx';
import { MarketCredits } from './MarketCredits/index.tsx';
import { MobileSeriesUserCurrencies } from './MobileSeriesUserCurrencies/index.tsx';
import { MobileUserCurrenciesDialog } from './MobileUserCurrenciesDialog/index.tsx';
import { MoneyBalances } from './MoneyBalances/index.tsx';
import { useExpandedCurrencies } from './useExpandedCurrencies.ts';
import type { InGameCurrencyAgreggated } from './useUserCurrencies.tsx';
import { useUserCurrencies } from './useUserCurrencies.tsx';

const Root = styled(Vertical)`
  position: relative;
  width: 100%;
`;
const InGameCurrenciesWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  &.collapsed {
    & > :not(:first-child) {
      margin-left: calc(-1 * var(--double-unit));
    }
  }
  @media ${laptopAndAbove} {
    .sidebarCollapsed & {
      flex-direction: column;
      & > * {
        width: 100%;
      }
    }

    &.collapsed > :not(:first-child) {
      margin-left: 0;
    }
  }
`;

type Props = {
  sidebarCollapsed?: boolean;
};
export const UserCurrencies = ({ sidebarCollapsed }: Props) => {
  const { showDrawer } = useWalletDrawerContext();
  const isLaptopAndAbove = useIsLaptop();
  const { sport } = useSportContext();
  const [mobileDialogOpened, setMobileDialogOpened] = useState(false);
  const [selectedCurrencyId, setSelectedCurrencyId] =
    useState<InGameCurrencyAgreggated | null>(null);
  const expandedCurrencies = useExpandedCurrencies();
  const currencies = useUserCurrencies(expandedCurrencies);
  const seriesNav = useSeriesNav();
  const selectedCurrency = currencies.find(
    currency => currency.id === selectedCurrencyId
  );

  const isMobileSeries = !isLaptopAndAbove && seriesNav;

  return (
    <>
      <MobileUserCurrenciesDialog
        open={mobileDialogOpened}
        onClose={() => setMobileDialogOpened(false)}
        currencies={currencies}
      />
      {isMobileSeries ? (
        <MobileSeriesUserCurrencies />
      ) : (
        <Root
          as={isLaptopAndAbove ? 'div' : 'button'}
          onClick={
            isLaptopAndAbove ? undefined : () => setMobileDialogOpened(true)
          }
        >
          <Vertical onMouseLeave={() => setSelectedCurrencyId(null)}>
            {!!isLaptopAndAbove && !sidebarCollapsed && (
              <FadeInOut>{selectedCurrency?.details?.({})}</FadeInOut>
            )}
            <InGameCurrenciesWrapper
              className={classNames({
                collapsed: !seriesNav,
              })}
            >
              {currencies.map((currency, i) => {
                const { key, visibleOnSports, amount } = currency;
                return (
                  visibleOnSports.includes(sport) &&
                  typeof amount === 'number' && (
                    <Currency
                      key={key}
                      currency={currency}
                      style={{ zIndex: currencies.length - i }}
                      selected={currency.id === selectedCurrency?.id}
                      onMouseEnter={() => setSelectedCurrencyId(currency.id)}
                    />
                  )
                );
              })}
            </InGameCurrenciesWrapper>
          </Vertical>
          {!!isLaptopAndAbove && !seriesNav && (
            <>
              <MoneyBalances
                onClick={showDrawer}
                sidebarCollapsed={sidebarCollapsed}
              />
              <button onClick={showDrawer} type="button">
                <MarketCredits
                  sport={sport}
                  sidebarCollapsed={sidebarCollapsed}
                />
              </button>
            </>
          )}
        </Root>
      )}
    </>
  );
};
